import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import './wastage_eom.css';
import MonthYearPicker from './MonthYearPicker';
import { exportSectionsToPDF } from '../utils/pdfExport';
import { banquetEventLabel } from '../utils/banquetWastageText';
import {
  A4_RATIO,
  FOOTER_RESERVE,
  PAGE_BUDGET,
  H,
  noteHeight,
  rowHeight,
  sectionHeight,
  paginateSections,
  pageHeader,
} from './wastage_eom.pages';
import {
  money,
  sourceBars,
  ownerRead,
  quadrantLede,
  masterLedger,
  lostSalesNote,
  categoryRows,
  categoryReading,
  buildControls,
} from './wastage_eom.view';

/**
 * WASTAGE — End of Month Report, laid out as the reference report: a cover, the executive
 * dashboard, the quadrant matrix over two sheets, Parts 1–5, the category ledger and next
 * month's controls. Long tables run onto "(CONT.)" pages, so the page count follows the
 * month's data and every page is exactly one A4 sheet in the PDF.
 *
 * This file is a RENDERER. Every money figure, bucket and quadrant comes from
 * /api/reports/wastage-eom (backend/routes/wastageEomReport.js); wastage_eom.view.js only
 * turns those figures into the sentences around them.
 */

function currentIstMonth() {
  const ist = new Date(Date.now() + 5.5 * 3600 * 1000);
  return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(month) {
  if (!month) return '';
  const [y, m] = month.split('-');
  const year = parseInt(y, 10);
  const monthNum = parseInt(m, 10);
  if (isNaN(year) || isNaN(monthNum)) return month;
  const lastDay = new Date(year, monthNum, 0).getDate();
  const pad = (n) => String(n).padStart(2, '0');
  return `01/${pad(monthNum)}/${year} – ${pad(lastDay)}/${pad(monthNum)}/${year}`;
}
/** 'YYYY-MM-DD' → 'DD/MM/YYYY'. */
const fmtDay = (d) => {
  const [y, m, day] = String(d || '').slice(0, 10).split('-');
  return y && m && day ? `${day}/${m}/${y}` : '';
};
const periodText = (period, month) =>
  period && period.startDate && period.endDate ? `${fmtDay(period.startDate)} – ${fmtDay(period.endDate)}` : monthLabel(month);

const barWidth = (value, max) => `${max > 0 ? (Math.max(Number(value) || 0, 0) / max) * 100 : 0}%`;

const SECTION = {
  summary: '01 — Executive Wastage Summary',
  matrix: '02 — Wastage Quadrant Matrix',
  part1: 'Part 1 — Master Food Wastage Ledger',
  part2: 'Part 2 — Non-Consumable (NC) Operational Wastage',
  part3: 'Part 3 — Semi-Processed Preparation Wastage',
  banquet: 'Banquet Wastage — Outside the Restaurant Figures',
  part4: 'Part 4 — The Sinking Fund Audit',
  part5: 'Part 5 — Cumulative Station Wastage Summary',
  category: '03 — Waste Category Ledger',
  controls: '04 — Operational Controls',
};

const QUADS = {
  q1: { key: 'q1', code: 'Q1', title: 'High Volume · High Profit Loss', tag: 'The System Drainers', rule: 'Retrain staff on portioning, cooking and handling at peak service.' },
  q3: { key: 'q3', code: 'Q3', title: 'High Volume · Low Profit Loss', tag: 'Operational Bulk Waste', rule: 'Align daily prep lists to actual historical sales.' },
  q2: { key: 'q2', code: 'Q2', title: 'Low Volume · High Profit Loss', tag: 'The High-Value Losses', rule: 'Lock high-value bins; require manager approval before discarding.' },
  q4: { key: 'q4', code: 'Q4', title: 'Low Volume · Low Profit Loss', tag: 'Minor Violations', rule: 'Monitor via standard inventory counts.' },
};

const TABLES = {
  quad: { className: 'weom-table weom-t-quad', head: ['Item / Ingredient', 'Wastage Qty', 'Unit Cost', 'Ingredient Cost', 'Lost Gross Profit'], height: H.tableHead },
  master: { className: 'weom-table weom-t-master', head: ['Item / Ingredient', 'Wastage Qty', 'Ingredient Cost', 'Lost Revenue Opp.', 'Lost Gross Profit', 'Primary Reason'], height: H.tableHead2 },
  nc: { className: 'weom-table weom-t-nc', head: ['Non-Consumable Item', 'Damaged Qty', 'Unit Cost', 'Total Capital Lost', 'Primary Reason'], height: H.tableHead },
  prep: { className: 'weom-table weom-t-prep', head: ['Semi-Processed Prep', 'Discarded Qty', 'Estimated Batch Cost', 'Lost Revenue Value', 'Lost Gross Profit', 'Primary Reason'], height: H.tableHead2 },
  staff: { className: 'weom-table weom-t-staff', head: ['Item Name', 'Destination', 'Quantity Given', 'Total Raw Material Cost'], height: H.tableHead },
  banquet: { className: 'weom-table weom-t-banquet', head: ['Item', 'Quantity', 'Cost'], height: H.tableHead },
};

const SUMMARY_LEDE =
  'This dashboard aggregates the month’s waste logs into macro metrics, so operational losses, raw product loss and missed revenue are visible before the detailed part ledgers.';
const LOW_VOLUME_LEDE =
  'Smaller quantities thrown away. Items with a high profit loss should need a manager’s approval before they are discarded.';
const MASTER_LEDE =
  'Raw ingredients and plated dishes thrown away instead of being sold — including items that expired or spoiled. Lost Gross Profit is the profit these would have earned had they been sold.';
const NC_LEDE =
  'Packaging, containers and branding material damaged or discarded during operations. These carry no menu price, so they show capital lost rather than lost revenue.';
const PREP_LEDE =
  'Batch items made in-house (sauces, chopped meats, marinades, doughs) that spoiled before use within their safe holding window.';
const BANQUET_LEDE =
  'Kept out of every restaurant figure in this report — banquet wastage comes off each banquet’s own gross profit.';
const STAFF_LEDE =
  'Food prepared and consumed by staff, management or the kitchen crew. Separating this from true floor wastage keeps the waste logs accurate while still tracking the raw cost of internal nourishment. These portions carry cost but no lost revenue — they were consumed on purpose.';
const STATIONS_LEDE =
  'Every food-waste type — master, spoilage and batch prep — totalled by the kitchen station that recorded it, to show which department drives the highest material loss.';
const CATEGORY_LEDE =
  'Every food-waste type totalled by category, with a direct read of raw purchase cost against the gross profit lost. Packaging carries no lost sale — its damage is the raw cost itself.';
const CATEGORY_NOTE =
  'Status compares each category with the month’s total waste cost: Critical is 40% or more, Watch is 15–40%, Low is under 15%. Spoilage / Expiry is stock recorded with an expiry or spoilage reason; it is listed with the floor wastage in Master Food Wastage (Part 1) but counted here on its own, and carries no lost sale because it went bad before it could be sold.';

// ---------------------------------------------------------------------------
// Page furniture
// ---------------------------------------------------------------------------
function Footer({ page, pages }) {
  return (
    <div className="weom-footer">
      Invexis Analytics · Wastage End-of-Month Report <span>|</span> Page {page} of {pages}
    </div>
  );
}

function Page({ ctx, page, header, continued, testid, flow, flowPage, children }) {
  return (
    <section
      className="weom-page"
      data-weom-section={testid}
      data-weom-flow={flow}
      data-weom-flow-page={flow ? flowPage : undefined}
    >
      {header.eyebrow && <p className="weom-eyebrow">{header.eyebrow}</p>}
      <h2>
        {header.title}
        {continued ? ' (CONT.)' : ''}
      </h2>
      {continued ? (
        <p className="weom-lede">Continued from the previous page.</p>
      ) : (
        header.lede && <p className="weom-lede">{header.lede}</p>
      )}
      {children}
      <Footer page={page} pages={ctx.pages} />
    </section>
  );
}

function SectionHead({ section }) {
  return (
    <div className="weom-section-head">
      <p className="weom-eyebrow">{section.eyebrow}</p>
      <h2>{section.title}</h2>
      {section.lede && <p className="weom-lede">{section.lede}</p>}
    </div>
  );
}

function Cover({ ctx }) {
  return (
    <section className="weom-page weom-cover" data-weom-section="cover">
      <h1>WASTAGE</h1>
      <p>End of Month Report</p>
      <span>{ctx.periodText}</span>
      <Footer page={1} pages={ctx.pages} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// 01 — Executive wastage summary
// ---------------------------------------------------------------------------
function Summary({ data, ctx, page }) {
  const s = data.summary;
  const boxes = [
    ['Box 1 — Total Raw Material Cost', s.raw_material_cost, 'Direct purchase cost of all raw, expired and semi-processed ingredients that hit the kitchen bin.'],
    ['Box 2 — Total NC Capital Loss', s.nc_capital_loss, 'Financial loss from damaged packaging, misprinted delivery boxes and spoiled carry bags.'],
    ['Box 3 — Lost Revenue Opportunity', s.lost_revenue_opportunity, 'Sales value that would have been collected if the wasted stock had become menu sales. Expired stock is not counted — it spoiled before it could be sold.'],
    ['Box 4 — Cumulative Lost Gross Profit', s.cumulative_lost_gross_profit, 'Gross profit lost through operational errors — lost sales value less the food cost behind it.'],
  ];
  const bars = sourceBars(data.ledger);
  const max = Math.max(...bars.map((b) => b.value), 0);
  return (
    <Page ctx={ctx} page={page} testid="summary" header={{ eyebrow: SECTION.summary, title: 'Monthly Waste Loss Dashboard', lede: SUMMARY_LEDE }}>
      <div className="weom-kpis">
        {boxes.map(([label, value, caption]) => (
          <div key={label} className="weom-box">
            <span className="weom-box-label">{label}</span>
            <strong className="weom-box-value">{money(value)}</strong>
            <p className="weom-box-caption">{caption}</p>
          </div>
        ))}
      </div>
      {/* Banquet wastage is the banquet's: the server keeps it out of all four boxes above,
          so the amount is named here rather than left to vanish. */}
      {Number(s.banquet_wastage_cost) > 0 && (
        <p className="weom-note">{`Banquet wastage (outside these figures): ${money(s.banquet_wastage_cost)} — see the banquet section after Part 3.`}</p>
      )}
      <div className="weom-chart">
        <h3>Total Monthly Capital Leak by Source (INR)</h3>
        {bars.map((b) => (
          <div className="weom-bar-row" key={b.key}>
            <span>{b.label}</span>
            <div>{b.value > 0 && <i className={`weom-pattern weom-pattern-${b.pattern}`} style={{ width: barWidth(b.value, max) }} />}</div>
            <strong>{money(b.value)}</strong>
          </div>
        ))}
      </div>
      <div className="weom-read">
        <h3>Owner Read</h3>
        <p>{ownerRead(s, data.ledger)}</p>
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Flow items — the long sections
// ---------------------------------------------------------------------------
const sectionItem = (key, eyebrow, title, lede, mid = false) => ({
  key,
  section: { eyebrow, title, lede },
  height: sectionHeight(lede) + (mid ? H.sectionGap : 0),
  keepWithNext: true,
});
const rowItem = (key, table, spec, node, height) => ({ key, table, spec, node, height });
const emptyItem = (key, table, spec, text) =>
  rowItem(key, table, spec, <tr><td colSpan={TABLES[spec].head.length} className="weom-empty">{text}</td></tr>, H.row);
const totalItem = (key, table, spec, cells) =>
  rowItem(key, table, spec, <tr className="weom-total">{cells.map((c, i) => <td key={i}>{c}</td>)}</tr>, H.totalRow);
/** The last data row stays with the total under it. */
const holdLast = (list) => {
  if (list.length) list[list.length - 1].keepWithNext = true;
  return list;
};

function QuadHead({ quad }) {
  return (
    <div className="weom-quad-head" data-testid={`quad-${quad.key}`}>
      <h3>
        <span>{quad.code} — {quad.title}</span>
        <em className="weom-quad-tag">{quad.tag}</em>
      </h3>
      <p className="weom-quad-rule">
        <strong>Action Rule:</strong> <em>{quad.rule}</em>
      </p>
    </div>
  );
}

function quadrantFlow(data, kind) {
  const q = data.quadrants || {};
  const items = kind === 'high'
    ? [sectionItem('quad-high-section', SECTION.matrix, 'Volume vs. Profit Loss', quadrantLede(q))]
    : [sectionItem('quad-low-section', SECTION.matrix, 'Low-Volume Wastage Items', LOW_VOLUME_LEDE)];
  const order = kind === 'high' ? [QUADS.q1, QUADS.q3] : [QUADS.q2, QUADS.q4];
  order.forEach((quad, n) => {
    items.push({ key: `${quad.key}-head`, node: <QuadHead quad={quad} />, height: H.quadHead + (n > 0 ? 14 : 0), keepWithNext: true });
    const list = Array.isArray(q[quad.key]) ? q[quad.key] : [];
    if (!list.length) items.push(emptyItem(`${quad.key}-empty`, quad.key, 'quad', 'No items fell into this quadrant this month.'));
    list.forEach((it, i) =>
      items.push(
        rowItem(
          `${quad.key}-${i}`,
          quad.key,
          'quad',
          <tr>
            <td>{it.item_name}</td>
            <td>{it.qty_display}</td>
            <td>{money(it.unit_cost)}</td>
            <td>{money(it.cost)}</td>
            <td>{money(it.lost_gp)}</td>
          </tr>,
          rowHeight(it.item_name, 28)
        )
      )
    );
  });
  return items;
}

function masterFlow(data) {
  const ledger = masterLedger(data);
  const items = [sectionItem('master-section', SECTION.part1, 'Master Food Wastage', MASTER_LEDE)];
  if (!ledger.rows.length) {
    items.push(emptyItem('master-empty', 'master', 'master', 'No food was recorded as wasted this month.'));
  } else {
    const rows = ledger.rows.map((it, i) =>
      rowItem(
        `master-${i}`,
        'master',
        'master',
        <tr>
          <td>{it.item_name}</td>
          <td>{it.qty_display}</td>
          <td>{money(it.cost)}</td>
          <td>{money(it.lost_revenue)}</td>
          <td>{money(it.lost_gp)}</td>
          <td className="weom-reason">{it.reason || '—'}</td>
        </tr>,
        Math.max(rowHeight(it.item_name, 17), rowHeight(it.reason, 15), rowHeight(it.qty_display, 15))
      )
    );
    items.push(...holdLast(rows));
    items.push(totalItem('master-total', 'master', 'master', ['Total', '', money(ledger.total.cost), money(ledger.total.lost_revenue), money(ledger.total.lost_gp), '']));
  }
  const note = lostSalesNote(data.summary, ledger.expiredCount > 0);
  items.push({ key: 'master-note', node: <p className="weom-note">{note}</p>, height: noteHeight(note) });
  return items;
}

function partsFlow(data) {
  const nc = data.part2_nc || { items: [], total: {} };
  const prep = data.part4_prep || { items: [], total: {} };
  const items = [sectionItem('nc-section', SECTION.part2, 'NC Packaging Loss', NC_LEDE)];

  if (!nc.items.length) {
    items.push(emptyItem('nc-empty', 'nc', 'nc', 'No packaging was damaged or discarded this month.'));
  } else {
    items.push(
      ...holdLast(
        nc.items.map((it, i) =>
          rowItem(
            `nc-${i}`,
            'nc',
            'nc',
            <tr>
              <td>{it.item_name}</td>
              <td>{it.qty_display}</td>
              <td>{money(it.unit_cost)}</td>
              <td>{money(it.cost)}</td>
              <td className="weom-reason">{it.reason || '—'}</td>
            </tr>,
            Math.max(rowHeight(it.item_name, 26), rowHeight(it.reason, 20))
          )
        )
      )
    );
    items.push(totalItem('nc-total', 'nc', 'nc', ['Total NC Capital Lost', '', '', money(nc.total.cost), '']));
  }

  items.push(sectionItem('prep-section', SECTION.part3, 'Batch / Prep Loss', PREP_LEDE, true));
  if (!prep.items.length) {
    items.push(emptyItem('prep-empty', 'prep', 'prep', 'No batch preparation was thrown away this month.'));
  } else {
    items.push(
      ...holdLast(
        prep.items.map((it, i) =>
          rowItem(
            `prep-${i}`,
            'prep',
            'prep',
            <tr>
              <td>{it.item_name}</td>
              <td>{it.qty_display}</td>
              <td>{money(it.cost)}</td>
              <td>{money(it.lost_revenue)}</td>
              <td>{money(it.lost_gp)}</td>
              <td className="weom-reason">{it.reason || '—'}</td>
            </tr>,
            Math.max(rowHeight(it.item_name, 16), rowHeight(it.reason, 15))
          )
        )
      )
    );
    items.push(totalItem('prep-total', 'prep', 'prep', ['Total Batch Loss', '', money(prep.total.cost), money(prep.total.lost_revenue), money(prep.total.lost_gp), '']));
  }

  // Banquet wastage: outside every restaurant figure, shown per event (server: part_banquet).
  const banquet = data.part_banquet;
  const events = banquet && Array.isArray(banquet.events) ? banquet.events : [];
  if (events.length) {
    items.push(sectionItem('banquet-section', SECTION.banquet, 'Banquet Wastage', BANQUET_LEDE, true));
    events.forEach((ev) => {
      const table = `banquet-${ev.eventId}`;
      items.push({
        key: `${table}-head`,
        node: <h3 className="weom-event-head">{banquetEventLabel(ev.functionName, ev.eventDate)}</h3>,
        height: H.eventHead,
        keepWithNext: true,
      });
      const list = Array.isArray(ev.items) ? ev.items : [];
      if (!list.length) items.push(emptyItem(`${table}-empty`, table, 'banquet', 'No records for this event.'));
      items.push(
        ...holdLast(
          list.map((it, i) =>
            rowItem(
              `${table}-${i}`,
              table,
              'banquet',
              <tr>
                <td>{it.item_name}</td>
                <td>{it.qty_display || Object.entries(it.qty_by_unit || {}).map(([u, qy]) => `${Number(qy).toFixed(2)} ${u}`).join(', ') || '—'}</td>
                <td>{money(it.cost)}</td>
              </tr>,
              rowHeight(it.item_name, 40)
            )
          )
        )
      );
      items.push(totalItem(`${table}-total`, table, 'banquet', ['Event Total', '', money(ev.total && ev.total.cost)]));
    });
    items.push({
      key: 'banquet-grand',
      node: (
        <p className="weom-grand">
          <span>Total Banquet Wastage</span>
          <strong>{money(banquet.total && banquet.total.cost)}</strong>
        </p>
      ),
      height: 64,
    });
  }
  return items;
}

function staffFlow(data) {
  const part = data.part5_staff_meals || { items: [], total: {}, tracked: false };
  const items = [sectionItem('staff-section', SECTION.part4, 'Staff Meals', STAFF_LEDE)];
  if (!part.tracked) {
    items.push({
      key: 'staff-none',
      node: (
        <div className="weom-notice">
          <h4>No staff meals logged this month</h4>
          <p>
            Staff meals are recorded by picking a <strong>Staff Meal</strong> reason (Kitchen Crew Meal, Floor Staff
            Meal, Management Tea / Coffee, Trainee Practice Plate) when logging stock-out. Any portion tagged that way
            appears here and is removed from the wastage loss totals instead of counting against you.
          </p>
        </div>
      ),
      height: H.notice,
    });
    return items;
  }
  items.push(
    ...holdLast(
      part.items.map((it, i) =>
        rowItem(
          `staff-${i}`,
          'staff',
          'staff',
          <tr>
            <td>{it.item_name}</td>
            <td className="weom-reason">{it.destination}</td>
            <td>{it.qty_display}</td>
            <td>{money(it.cost)}</td>
          </tr>,
          Math.max(rowHeight(it.item_name, 26), rowHeight(it.destination, 24))
        )
      )
    )
  );
  items.push(totalItem('staff-total', 'staff', 'staff', ['Total Staff Meal Cost', '', '', money(part.total.cost)]));
  return items;
}

/** Lay a page's slice of a flow out: consecutive rows of one table become one table. */
function FlowBody({ items }) {
  const out = [];
  let run = null;
  const flush = () => {
    if (!run) return;
    const spec = TABLES[run.spec];
    out.push(
      <table className={spec.className} key={`t-${run.items[0].key}`}>
        <thead>
          <tr>
            {spec.head.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{run.items.map((it) => React.cloneElement(it.node, { key: it.key }))}</tbody>
      </table>
    );
    run = null;
  };
  for (const it of items) {
    if (it.table) {
      if (!run || run.table !== it.table) {
        flush();
        run = { table: it.table, spec: it.spec, items: [] };
      }
      run.items.push(it);
    } else {
      flush();
      out.push(it.section ? <SectionHead key={it.key} section={it.section} /> : <React.Fragment key={it.key}>{it.node}</React.Fragment>);
    }
  }
  flush();
  return out;
}

// ---------------------------------------------------------------------------
// Part 5 — stations
// ---------------------------------------------------------------------------
function Stations({ data, ctx, page }) {
  const part = data.part6_stations || {};
  const stations = Array.isArray(part.stations) ? part.stations : [];
  const box1 = Number(data.summary.raw_material_cost) || 0;
  return (
    <Page ctx={ctx} page={page} testid="stations" header={{ eyebrow: SECTION.part5, title: 'Loss by Kitchen Station', lede: STATIONS_LEDE }}>
      {part.available && stations.length ? (
        <table className="weom-table weom-t-stations">
          <thead>
            <tr><th>Station</th><th>Quantities Discarded</th><th>Raw Cost</th><th>Lost Revenue Opp.</th><th>Lost Gross Profit</th><th>Share</th></tr>
          </thead>
          <tbody>
            {stations.map((st) => (
              <tr key={st.station_name}>
                <td>{st.station_name}</td><td>{st.qty_display}</td><td>{money(st.cost)}</td>
                <td>{money(st.lost_revenue)}</td><td>{money(st.lost_gp)}</td>
                <td>{box1 > 0 ? `${Math.round((Number(st.cost) / box1) * 100)}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="weom-notice">
          <h4>Station-level wastage is not tracked yet</h4>
          {part.reason && <p>{part.reason}</p>}
          {part.enable_hint && <p className="weom-notice-hint">{part.enable_hint}</p>}
          <p className="weom-notice-hint">
            This table is left empty rather than filled by guessing a station from the item category. Every other
            figure in this report is traced back to a recorded wastage row; a station split would have to be invented,
            and it would read as fact while being a guess.
          </p>
        </div>
      )}
    </Page>
  );
}

// ---------------------------------------------------------------------------
// 03 — Waste category ledger
// ---------------------------------------------------------------------------
function Category({ data, ctx, page }) {
  const { rows } = categoryRows(data.ledger);
  const max = Math.max(...rows.flatMap((r) => [r.cost, r.profit]), 0);
  return (
    <Page ctx={ctx} page={page} testid="category" header={{ eyebrow: SECTION.category, title: 'Capital Leak by Category', lede: CATEGORY_LEDE }}>
      <table className="weom-table weom-t-category">
        <thead>
          <tr><th>Waste Category</th><th>Raw Cost</th><th>Lost Sales</th><th>Lost Gross Profit</th><th>Status</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td>{r.label}</td><td>{money(r.cost)}</td><td>{money(r.revenue)}</td><td>{money(r.profit)}</td><td>{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="weom-note">{CATEGORY_NOTE}</p>

      <h3 className="weom-subhead">Loss Composition — Raw Cost vs GP Lost</h3>
      <div className="weom-mix">
        {rows.map((r) => (
          <div key={r.key} className="weom-mix-row">
            <span className="weom-mix-name">{r.label}</span>
            <div className="weom-mix-bars">
              <div className="weom-mix-bar">
                <span>Raw Cost</span>
                <div className="weom-mix-track"><i className="weom-mix-raw" style={{ width: barWidth(r.cost, max) }} /></div>
                <strong>{money(r.cost)}</strong>
              </div>
              <div className="weom-mix-bar">
                <span>GP Impact</span>
                <div className="weom-mix-track"><i className="weom-mix-gp" style={{ width: barWidth(r.profit, max) }} /></div>
                <strong>{money(r.profit)}</strong>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="weom-read">
        <h3>Reading This</h3>
        <p>{categoryReading(data.ledger)}</p>
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------------------
// 04 — Next month waste controls
// ---------------------------------------------------------------------------
function Controls({ data, ctx, page }) {
  const { oneLine, blocks } = buildControls(data);
  return (
    <Page ctx={ctx} page={page} testid="controls" header={{ eyebrow: SECTION.controls, title: 'Next Month Waste Controls' }}>
      <div className="weom-read weom-oneline">
        <h3>In One Line</h3>
        <p>{oneLine}</p>
      </div>
      {blocks.length > 0 && (
        <div className="weom-controls-list">
          {blocks.map((b) => (
            <div className="weom-control" key={b.key}>
              <h3>{b.title}</h3>
              <p><strong>What happened:</strong> {b.happened}</p>
              <p><strong>What to do:</strong> {b.todo}</p>
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

// ---------------------------------------------------------------------------
// Page list
// ---------------------------------------------------------------------------
const NO_SHAVES = {};
/** Enough for any month: each pass moves at least one item forward. */
const MAX_MEASURE_PASSES = 60;
/** Below this the page is a phone layout, not an A4 sheet, and is not measured. */
const MIN_MEASURE_WIDTH = 700;
const flowPageId = (flow, index) => (index === 0 ? flow : `${flow}-${index + 1}`);

/**
 * Every page this month's data produces, in reading order. `shaves` holds measured overflow,
 * in pixels, per flow page (`${flow}:${index}`); it only ever shrinks a page's budget, so
 * items move forward and never back.
 */
function buildPageList(data, shaves) {
  if (!data) return [];
  const pages = [{ id: 'cover', kind: 'cover' }, { id: 'summary', kind: 'summary' }];
  const flow = (name, items) => {
    const tableHead = (table) => {
      const it = items.find((x) => x.table === table);
      return it ? TABLES[it.spec].height : H.tableHead;
    };
    const budget = (index) => PAGE_BUDGET - (shaves[`${name}:${index}`] || 0);
    paginateSections(items, budget, tableHead).forEach((slice, index) => {
      pages.push({ id: flowPageId(name, index), kind: 'flow', flow: name, flowPage: index, ...pageHeader(items, slice.from, slice.to) });
    });
  };
  flow('quad-high', quadrantFlow(data, 'high'));
  flow('quad-low', quadrantFlow(data, 'low'));
  flow('master', masterFlow(data));
  flow('parts', partsFlow(data));
  flow('staff', staffFlow(data));
  pages.push({ id: 'stations', kind: 'stations' }, { id: 'category', kind: 'category' }, { id: 'controls', kind: 'controls' });
  return pages;
}

// ------------------------------------------------------------------ main
export default function WastageEom({ goTo }) {
  const [month, setMonth] = useState(currentIstMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gated, setGated] = useState(false);
  const [gateDismissed, setGateDismissed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const rootRef = useRef(null);
  const maxMonth = currentIstMonth();

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null); setGated(false); setGateDismissed(false); setData(null);
    try {
      const businessId = localStorage.getItem('businessId');
      const token = localStorage.getItem('token');
      if (!businessId) { setError('No business selected'); setLoading(false); return; }

      const res = await fetch(`/api/reports/wastage-eom?businessId=${businessId}&month=${month}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const result = await res.json();

      if (result.gated) { setGated(true); }
      else if (result.success) { setData(result.data); }
      else { setError(result.message || 'Failed to load report'); }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ---- Page breaking ------------------------------------------------------
  // Estimated breaks first (wastage_eom.pages.js), then measured: any page whose content
  // runs into the footer zone has its budget shrunk by the overflow and the list is rebuilt,
  // so items move forward until every page fits one A4 sheet. Corrections belong to the
  // data they were measured against and are dropped when the data or the width changes.
  const [shaveState, setShaveState] = useState({ data: null, map: {} });
  const shaves = shaveState.data === data ? shaveState.map : NO_SHAVES;
  const passes = useRef({ data: null, count: 0 });
  const [measureTick, setMeasureTick] = useState(0);

  const pageList = useMemo(() => buildPageList(data, shaves), [data, shaves]);

  useEffect(() => {
    // Web fonts change line heights; measure again once they are in.
    let alive = true;
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => alive && setMeasureTick((n) => n + 1));
    }
    let timer = null;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // A new width means new line wraps: start the corrections over.
        setShaveState({ data: null, map: {} });
        passes.current = { data: null, count: 0 };
        setMeasureTick((n) => n + 1);
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      alive = false;
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !data) return;
    if (passes.current.data !== data) passes.current = { data, count: 0 };
    if (passes.current.count >= MAX_MEASURE_PASSES) return;

    for (const el of root.querySelectorAll('[data-weom-flow]')) {
      const width = el.offsetWidth;
      // No layout (jsdom) or a phone-width page: the estimates stand.
      if (!width || width < MIN_MEASURE_WIDTH) return;
      const limit = width * A4_RATIO - FOOTER_RESERVE;
      const top = el.getBoundingClientRect().top;
      let bottom = 0;
      for (const child of el.children) {
        if (child.classList.contains('weom-footer')) continue;
        bottom = Math.max(bottom, child.getBoundingClientRect().bottom - top);
      }
      if (bottom > limit) {
        const key = `${el.dataset.weomFlow}:${el.dataset.weomFlowPage}`;
        const overflow = Math.ceil(bottom - limit) + 4;
        passes.current.count += 1;
        setShaveState((st) => {
          const map = st.data === data ? st.map : {};
          return { data, map: { ...map, [key]: (map[key] || 0) + overflow } };
        });
        return;
      }
    }
  }, [data, pageList, measureTick]);

  /** Every page that rendered, in reading order — each one A4 sheet, each one PDF page. */
  const downloadPdf = async () => {
    if (!rootRef.current || exporting) return;
    setExporting(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const sectionSelectors = [...rootRef.current.querySelectorAll('[data-weom-section]')].map(
        (el) => `[data-weom-section="${el.dataset.weomSection}"]`
      );
      await exportSectionsToPDF({
        container: rootRef.current,
        sectionSelectors,
        title: 'Wastage EOM Report',
        periodLabel: periodText(data && data.period, month),
        fullPageMode: true,
        forcePageBreakPerSection: true,
        hideSelectorsDuringCapture: ['.weom-controls'],
      });
    } catch (e) {
      alert('Could not generate PDF: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  const controls = (
    <div className="weom-controls">
      <div className="weom-controls-left">
        <label htmlFor="weom-month">Report month</label>
        <button id="weom-month" type="button" className="weom-month-btn" onClick={() => setPickerOpen(true)}>
          {monthLabel(month)} <span className="weom-caret">▾</span>
        </button>
      </div>
      {data && !gated && (
        <button className="weom-download" onClick={downloadPdf} disabled={exporting}>
          {exporting ? 'Preparing PDF…' : '⭳ Download PDF'}
        </button>
      )}
    </div>
  );

  const ctx = { pages: pageList.length, periodText: periodText(data && data.period, month) };

  const renderPage = (pg, index) => {
    const page = index + 1;
    switch (pg.kind) {
      case 'cover':
        return <Cover key={pg.id} ctx={ctx} />;
      case 'summary':
        return <Summary key={pg.id} data={data} ctx={ctx} page={page} />;
      case 'flow':
        return (
          <Page
            key={pg.id}
            ctx={ctx}
            page={page}
            header={pg.section}
            continued={pg.continued}
            testid={pg.id}
            flow={pg.flow}
            flowPage={pg.flowPage}
          >
            <FlowBody items={pg.body} />
          </Page>
        );
      case 'stations':
        return <Stations key={pg.id} data={data} ctx={ctx} page={page} />;
      case 'category':
        return <Category key={pg.id} data={data} ctx={ctx} page={page} />;
      case 'controls':
        return <Controls key={pg.id} data={data} ctx={ctx} page={page} />;
      default:
        return null;
    }
  };

  return (
    <main className="weom-root" ref={rootRef}>
      {controls}
      <MonthYearPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        selected={month}
        maxMonth={maxMonth}
        onSelect={setMonth}
      />

      {loading && <div className="weom-state">Loading wastage report for {monthLabel(month)}…</div>}
      {error && !loading && <div className="weom-state weom-state-error">Error: {error}</div>}

      {gated && !loading && gateDismissed && (
        <div className="weom-state">Closing stock for {monthLabel(month)} isn’t submitted. Pick another month above, or give closing stock to generate this report.</div>
      )}

      {gated && !loading && !gateDismissed && (
        <div className="weom-gate">
          <div className="weom-gate-card">
            <div className="weom-gate-icon">🔒</div>
            <h3>Closing stock required</h3>
            <p>The Wastage End-of-Month report for <strong>{monthLabel(month)}</strong> can only be generated after you submit closing stock for that month.</p>
            <div className="weom-gate-actions">
              <button className="weom-gate-primary" onClick={() => goTo && goTo('closing-stock')}>Give Closing Stock</button>
              <button className="weom-gate-secondary" onClick={() => setGateDismissed(true)}>Choose another month</button>
            </div>
          </div>
        </div>
      )}

      {data && !loading && !gated && pageList.map(renderPage)}
    </main>
  );
}
